<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Dashboard",
};
</script>

<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import ChildService from "@/api/child";
import UserService from "@/api/user";
import FamilyService from "@/api/family";
import DealerService from "@/api/dealer";
import DeviceService from "@/api/device";
import LicenseService from "@/api/license";
import { onMounted, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useSnackStore } from "@/stores/snack";
import { useI18n } from "vue-i18n";

definePage({
  meta: {
    title: "Dashboard",
  },
});

const { t } = useI18n();
const snackStore = useSnackStore();
const fetchEnabled = ref(true);

type TDashboardItem = {
  total: number;
  icon: string;
};

type TDashboard = {
  children: TDashboardItem;
  users: TDashboardItem;
  families: TDashboardItem;
  dealers: TDashboardItem;
  devices: TDashboardItem;
  licenses: TDashboardItem;
};

const {
  data: dashboardStats,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["dashboard"],
  queryFn: async (): Promise<TDashboard | undefined> => {
    try {
      const [childTotal, userTotal, familyTotal, dealerTotal, deviceTotal, licenseTotal] = await Promise.all([
        ChildService.child.getTotal(),
        UserService.user.getTotal(),
        FamilyService.family.getTotal(),
        DealerService.dealer.getTotal(),
        DeviceService.device.getTotal(),
        LicenseService.license.getTotal(),
      ]);

      let dashboard: TDashboard = {
        children: {
          total: childTotal,
          icon: "mdi-baby-carriage",
        },
        users: {
          total: userTotal,
          icon: "mdi-human-male-female",
        },
        families: {
          total: familyTotal,
          icon: "mdi-human-male-female-child",
        },
        dealers: {
          total: dealerTotal,
          icon: "mdi-account-tie",
        },
        devices: {
          total: deviceTotal,
          icon: "mdi-devices",
        },
        licenses: {
          total: licenseTotal,
          icon: "mdi-receipt-text-clock",
        },
      };

      return dashboard;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => fetchEnabled.value,
});

onMounted(async () => {
  fetchEnabled.value = false;
});
</script>

<template>
  <v-container
    fluid
    class="tw:h-full"
  >
    <v-row class="tw:h-full tw:align-stretch">
      <v-col
        v-for="(item, key) in dashboardStats"
        :key="key"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          class="tw:h-full tw:!flex tw:items-center tw:text-center tw:!bg-white/5"
          :to="`/${key}`"
        >
          <v-card-text>
            <v-icon
              size="7vh"
              class="tw:!text-white/75"
              >{{ item.icon }}</v-icon
            >
            <div class="tw:text-sm tw:font-medium tw:!mt-2 text-primary">{{ t(`menu.${key}`) }}</div>
            <div class="tw:text-6xl tw:!mt-7 tw:font-bold text-white">
              {{ item.total }}<span class="tw:text-3xl tw:!ml-1">x</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped></style>
